import { useState, useEffect, useMemo, useCallback } from "react";
import api, { isAuthError, formatApiError } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/utils/roles";
import { toast } from "sonner";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sun, Wallet, ClipboardList, Plane, CheckCircle2, XCircle, Timer, CalendarDays, Sparkles, Settings as SettingsIcon, Users } from "lucide-react";
import StaffCalendar from "@/components/StaffCalendar";

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

function fmtRange(a, b) {
  return a === b ? a : `${a} → ${b}`;
}

export default function HolidaysPage() {
  useDocumentTitle("Holidays", "Book holidays, TOIL and manage approvals.");
  const { user } = useAuth();
  const canManage = isSuperAdmin(user?.role);

  const [tab, setTab] = useState("mine");
  const [entitlement, setEntitlement] = useState(null);
  const [bankHols, setBankHols] = useState([]);
  const [config, setConfig] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [toilEntries, setToilEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // New request form
  const [reqStart, setReqStart] = useState("");
  const [reqEnd, setReqEnd] = useState("");
  const [reqType, setReqType] = useState("holiday");
  const [reqNotes, setReqNotes] = useState("");
  const [reqHalfStart, setReqHalfStart] = useState("");  // "", "am", "pm"
  const [reqHalfEnd, setReqHalfEnd] = useState("");
  const [submittingReq, setSubmittingReq] = useState(false);

  // TOIL form
  const [toilHours, setToilHours] = useState("");
  const [toilDate, setToilDate] = useState("");
  const [toilDesc, setToilDesc] = useState("");
  const [submittingToil, setSubmittingToil] = useState(false);

  // Reject dialog
  const [rejectFor, setRejectFor] = useState(null); // {kind:"holiday"|"toil", id}
  const [rejectReason, setRejectReason] = useState("");

  // Config editor
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfgDraft, setCfgDraft] = useState({});

  // Christmas dialog
  const [xmasOpen, setXmasOpen] = useState(false);
  const [xmasYear, setXmasYear] = useState(new Date().getFullYear());

  const load = useCallback(async () => {
    try {
      const calls = [
        api.get("/holidays/entitlement"),
        api.get("/holidays/config"),
        api.get(`/holidays/bank-holidays?year=${new Date().getFullYear()}`),
        api.get("/holidays/requests?scope=mine"),
        api.get("/holidays/toil?scope=mine"),
      ];
      if (canManage) calls.push(api.get("/holidays/requests?scope=all"));
      const results = await Promise.all(calls);
      setEntitlement(results[0].data);
      setConfig(results[1].data);
      setBankHols(results[2].data.holidays || []);
      setMyRequests(results[3].data.requests || []);
      setToilEntries(results[4].data.toil || []);
      if (canManage && results[5]) setTeamRequests(results[5].data.requests || []);
    } catch (err) {
      if (!isAuthError(err)) toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => { load(); }, [load]);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!reqStart || !reqEnd) { toast.error("Pick start and end dates"); return; }
    setSubmittingReq(true);
    try {
      await api.post("/holidays/requests", {
        start_date: reqStart, end_date: reqEnd, type: reqType, notes: reqNotes,
        half_start: reqHalfStart, half_end: reqHalfEnd,
      });
      toast.success("Request submitted");
      setReqStart(""); setReqEnd(""); setReqNotes(""); setReqType("holiday");
      setReqHalfStart(""); setReqHalfEnd("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmittingReq(false);
    }
  };

  const submitToil = async (e) => {
    e.preventDefault();
    if (!toilHours || !toilDate) { toast.error("Enter hours and worked date"); return; }
    setSubmittingToil(true);
    try {
      await api.post("/holidays/toil", {
        hours: parseFloat(toilHours), worked_date: toilDate, description: toilDesc,
      });
      toast.success("TOIL entry submitted");
      setToilHours(""); setToilDate(""); setToilDesc("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmittingToil(false);
    }
  };

  const cancelRequest = async (id) => {
    if (!window.confirm("Cancel this request?")) return;
    try {
      await api.delete(`/holidays/requests/${id}`);
      toast.success("Cancelled");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const approve = async (kind, id) => {
    try {
      await api.post(`/holidays/${kind === "toil" ? "toil" : "requests"}/${id}/approve`);
      toast.success("Approved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const openReject = (kind, id) => { setRejectFor({ kind, id }); setRejectReason(""); };
  const submitReject = async () => {
    if (!rejectReason.trim()) { toast.error("Give a rejection reason"); return; }
    try {
      const path = rejectFor.kind === "toil" ? "toil" : "requests";
      await api.post(`/holidays/${path}/${rejectFor.id}/reject`, { rejection_reason: rejectReason });
      toast.success("Rejected");
      setRejectFor(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const openConfig = () => { setCfgDraft(config || {}); setCfgOpen(true); };
  const saveConfig = async () => {
    try {
      const payload = {
        manager_email: cfgDraft.manager_email || "",
        ceo_email: cfgDraft.ceo_email || "",
        annual_entitlement_days: parseInt(cfgDraft.annual_entitlement_days) || 20,
        christmas_closure_days: parseInt(cfgDraft.christmas_closure_days) || 3,
      };
      const { data } = await api.put("/holidays/config", payload);
      setConfig(data);
      toast.success("Config saved");
      setCfgOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const runChristmas = async () => {
    if (!window.confirm(`Auto-book Christmas closure for ${xmasYear}? Every staff/admin will get an email.`)) return;
    try {
      const { data } = await api.post("/holidays/auto-christmas", { year: xmasYear });
      toast.success(`Booked ${data.booked}, skipped ${data.skipped_existing}, emailed ${data.emailed}`);
      setXmasOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const pendingTeam = useMemo(
    () => (teamRequests || []).filter((r) => r.status === "pending"),
    [teamRequests]
  );

  if (loading) return <div className="animate-pulse text-muted-foreground" data-testid="holidays-loading">Loading holidays…</div>;

  const ent = entitlement || {};
  const usedPct = ent.annual_entitlement_days ? Math.min(100, (ent.days_used / ent.annual_entitlement_days) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="holidays-page">
      {/* Entitlement summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="border border-sky-200 rounded-md p-4 bg-sky-50" data-testid="kpi-entitlement">
          <p className="text-xs text-sky-800 uppercase tracking-wide mb-1 flex items-center gap-1.5"><Plane size={12}/> Entitlement — FY {ent.fy_label}</p>
          <p className="font-heading font-bold text-2xl text-sky-700">{ent.annual_entitlement_days} days</p>
          <p className="text-[11px] text-sky-600 mt-0.5">Refreshes every Aug 1</p>
        </div>
        <div className="border border-emerald-200 rounded-md p-4 bg-emerald-50" data-testid="kpi-days-remaining">
          <p className="text-xs text-emerald-800 uppercase tracking-wide mb-1 flex items-center gap-1.5"><CheckCircle2 size={12}/> Days remaining</p>
          <p className="font-heading font-bold text-2xl text-emerald-700">{ent.days_remaining}</p>
          <div className="h-1.5 mt-2 bg-white/70 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${100 - usedPct}%` }} />
          </div>
        </div>
        <div className="border border-slate-200 rounded-md p-4 bg-white" data-testid="kpi-days-used">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5"><CalendarDays size={12}/> Days used</p>
          <p className="font-heading font-bold text-2xl">{ent.days_used}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Approved bookings this FY</p>
        </div>
        <div className="border border-violet-200 rounded-md p-4 bg-violet-50" data-testid="kpi-toil-balance">
          <p className="text-xs text-violet-800 uppercase tracking-wide mb-1 flex items-center gap-1.5"><Timer size={12}/> TOIL balance</p>
          <p className="font-heading font-bold text-2xl text-violet-700">{ent.toil_hours_balance}h</p>
          <p className="text-[11px] text-violet-600 mt-0.5">{ent.toil_hours_accrued}h accrued · {ent.toil_hours_used}h used</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <TabsList className="h-8" data-testid="holidays-tabs">
            <TabsTrigger value="mine" className="text-xs px-3 py-1"><Sun size={12} className="mr-1"/> My holidays</TabsTrigger>
            <TabsTrigger value="toil" className="text-xs px-3 py-1"><Wallet size={12} className="mr-1"/> TOIL wallet</TabsTrigger>
            {canManage && <TabsTrigger value="team" className="text-xs px-3 py-1"><ClipboardList size={12} className="mr-1"/> Team approvals {pendingTeam.length > 0 && <Badge variant="outline" className="ml-1 text-[10px] bg-amber-100 text-amber-700 border-amber-200">{pendingTeam.length}</Badge>}</TabsTrigger>}
            {canManage && <TabsTrigger value="team-cal" className="text-xs px-3 py-1"><Users size={12} className="mr-1"/> Team month view</TabsTrigger>}
            <TabsTrigger value="calendar" className="text-xs px-3 py-1"><CalendarDays size={12} className="mr-1"/> Bank holidays</TabsTrigger>
          </TabsList>
          {canManage && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setXmasOpen(true)} data-testid="xmas-btn">
                <Sparkles size={12} /> Auto-book Christmas
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={openConfig} data-testid="config-btn">
                <SettingsIcon size={12} /> Config
              </Button>
            </div>
          )}
        </div>

        {/* MY HOLIDAYS */}
        <TabsContent value="mine" className="space-y-4 mt-4">
          <form onSubmit={submitRequest} className="border border-border rounded-md p-4 bg-white grid grid-cols-1 sm:grid-cols-5 gap-3" data-testid="new-request-form">
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Start</label>
              <Input type="date" value={reqStart} onChange={(e) => setReqStart(e.target.value)} className="h-9 text-sm" data-testid="req-start" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wide">End</label>
              <Input type="date" value={reqEnd} onChange={(e) => setReqEnd(e.target.value)} className="h-9 text-sm" data-testid="req-end" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Type</label>
              <select value={reqType} onChange={(e) => setReqType(e.target.value)} className="h-9 w-full text-sm border border-border rounded-md bg-white px-2" data-testid="req-type">
                <option value="holiday">Annual leave</option>
                <option value="toil">TOIL (uses TOIL balance)</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Notes</label>
              <Input value={reqNotes} onChange={(e) => setReqNotes(e.target.value)} placeholder="Optional reason / cover arrangements" className="h-9 text-sm" data-testid="req-notes" />
            </div>
            <div className="sm:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground uppercase tracking-wide">First day half?</label>
                <div className="flex gap-1 mt-1" data-testid="req-half-start-group">
                  {["", "am", "pm"].map(v => (
                    <button key={v || "full"} type="button" onClick={() => setReqHalfStart(v)}
                      className={`h-8 px-3 text-xs rounded-md border ${reqHalfStart === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-border hover:bg-slate-50"}`}
                      data-testid={`req-half-start-${v || "full"}`}>
                      {v ? v.toUpperCase() + " only" : "Full day"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Last day half?</label>
                <div className="flex gap-1 mt-1" data-testid="req-half-end-group">
                  {["", "am", "pm"].map(v => (
                    <button key={v || "full"} type="button" onClick={() => setReqHalfEnd(v)}
                      className={`h-8 px-3 text-xs rounded-md border ${reqHalfEnd === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-border hover:bg-slate-50"}`}
                      data-testid={`req-half-end-${v || "full"}`}>
                      {v ? v.toUpperCase() + " only" : "Full day"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="sm:col-span-5 flex justify-end">
              <Button type="submit" disabled={submittingReq} className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white" data-testid="req-submit">
                {submittingReq ? "Submitting…" : "Submit request"}
              </Button>
            </div>
          </form>

          <RequestTable rows={myRequests} scope="mine" onCancel={cancelRequest} />
        </TabsContent>

        {/* TOIL */}
        <TabsContent value="toil" className="space-y-4 mt-4">
          <form onSubmit={submitToil} className="border border-border rounded-md p-4 bg-white grid grid-cols-1 sm:grid-cols-5 gap-3" data-testid="new-toil-form">
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Hours worked</label>
              <Input type="number" step="0.25" min="0.25" max="24" value={toilHours} onChange={(e) => setToilHours(e.target.value)} className="h-9 text-sm" data-testid="toil-hours" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Worked date</label>
              <Input type="date" value={toilDate} onChange={(e) => setToilDate(e.target.value)} className="h-9 text-sm" data-testid="toil-date" />
            </div>
            <div className="sm:col-span-3">
              <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Description</label>
              <Input value={toilDesc} onChange={(e) => setToilDesc(e.target.value)} placeholder="e.g. Worked New Year's Day on-call" className="h-9 text-sm" data-testid="toil-desc" />
            </div>
            <div className="sm:col-span-5 flex justify-end">
              <Button type="submit" disabled={submittingToil} className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white" data-testid="toil-submit">
                {submittingToil ? "Submitting…" : "Log TOIL"}
              </Button>
            </div>
          </form>

          <ToilTable rows={toilEntries} />
        </TabsContent>

        {/* TEAM APPROVALS */}
        {canManage && (
          <TabsContent value="team" className="space-y-4 mt-4">
            <TeamSummary rows={teamRequests} />
            {pendingTeam.length === 0 ? (
              <div className="border border-border rounded-md p-6 bg-white text-center text-sm text-muted-foreground" data-testid="team-empty">
                Nothing pending — the team is all caught up.
              </div>
            ) : (
              <>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-2">
                  <ClipboardList size={12}/> Awaiting your approval ({pendingTeam.length})
                </h3>
                <RequestTable rows={pendingTeam} scope="team" onApprove={(id) => approve("holiday", id)} onReject={(id) => openReject("holiday", id)} />
              </>
            )}
            <div className="pt-2">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">All team requests (recent)</h3>
              <RequestTable rows={teamRequests} scope="team-all" />
            </div>
          </TabsContent>
        )}

        {/* TEAM MONTH VIEW (superadmin only) — embeds StaffCalendar so
            Jamie can see every approved / pending / auto holiday alongside
            sick / training rows on one grid. Colour legend is the same. */}
        {canManage && (
          <TabsContent value="team-cal" className="mt-4">
            <StaffCalendar isStaff={true} />
          </TabsContent>
        )}

        {/* BANK HOLIDAYS */}
        <TabsContent value="calendar" className="space-y-4 mt-4">
          <div className="border border-border rounded-md p-4 bg-white" data-testid="bank-holidays-list">
            <h3 className="font-heading font-semibold text-sm mb-3">Scottish bank holidays — {config?.year}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {bankHols.map(b => (
                <div key={b.date} className="flex items-center justify-between border border-border rounded px-3 py-2 bg-slate-50">
                  <div>
                    <p className="text-sm font-medium">{b.name}</p>
                    <p className="text-[11px] text-muted-foreground">{new Date(b.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">Free day</Badge>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Bank holidays don't count against your 20-day allowance.</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Reject dialog */}
      <Dialog open={rejectFor !== null} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reject with reason</DialogTitle></DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Explain why so the requester knows what to do next…" className="min-h-[120px]" data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submitReject} data-testid="reject-confirm">Reject request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config dialog */}
      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="max-w-lg" data-testid="cfg-dialog">
          <DialogHeader><DialogTitle className="font-heading">Holiday module config</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Manager email (approves everyone)</label>
              <Input value={cfgDraft.manager_email || ""} onChange={(e) => setCfgDraft({ ...cfgDraft, manager_email: e.target.value })} data-testid="cfg-manager" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wide">CEO email (auto-approved, approves manager)</label>
              <Input value={cfgDraft.ceo_email || ""} onChange={(e) => setCfgDraft({ ...cfgDraft, ceo_email: e.target.value })} data-testid="cfg-ceo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Annual entitlement (days)</label>
                <Input type="number" min="0" max="60" value={cfgDraft.annual_entitlement_days ?? 20} onChange={(e) => setCfgDraft({ ...cfgDraft, annual_entitlement_days: e.target.value })} data-testid="cfg-days" />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Christmas closure (days)</label>
                <Input type="number" min="0" max="14" value={cfgDraft.christmas_closure_days ?? 3} onChange={(e) => setCfgDraft({ ...cfgDraft, christmas_closure_days: e.target.value })} data-testid="cfg-xmas-days" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCfgOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={saveConfig} data-testid="cfg-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Xmas dialog */}
      <Dialog open={xmasOpen} onOpenChange={setXmasOpen}>
        <DialogContent className="max-w-md" data-testid="xmas-dialog">
          <DialogHeader><DialogTitle className="font-heading">Auto-book Christmas closure</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Insert an auto-approved holiday for every live staff/admin covering the {config?.christmas_closure_days || 3} working days between Christmas and New Year. Each person will get an email.</p>
          <div className="mt-3">
            <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Year</label>
            <Input type="number" min="2024" max="2030" value={xmasYear} onChange={(e) => setXmasYear(parseInt(e.target.value) || xmasYear)} data-testid="xmas-year" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setXmasOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={runChristmas} data-testid="xmas-run">Run auto-book</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RequestTable({ rows, scope, onCancel, onApprove, onReject }) {
  if (!rows || rows.length === 0) return (
    <div className="border border-border rounded-md p-6 bg-white text-center text-sm text-muted-foreground" data-testid="requests-empty">
      No requests yet.
    </div>
  );
  return (
    <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid={`requests-table-${scope}`}>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left">
          {scope !== "mine" && <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Who</th>}
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Dates</th>
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Days</th>
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Type</th>
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Status</th>
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Notes</th>
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs text-right">Actions</th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-border" data-testid={`req-row-${r.id}`}>
              {scope !== "mine" && <td className="px-4 py-2"><div className="font-medium">{r.user_name}</div><div className="text-[11px] text-muted-foreground">{r.user_email}</div></td>}
              <td className="px-4 py-2 whitespace-nowrap">{fmtRange(r.start_date, r.end_date)}</td>
              <td className="px-4 py-2">{r.working_days}</td>
              <td className="px-4 py-2 capitalize">{r.type}</td>
              <td className="px-4 py-2">
                <Badge variant="outline" className={STATUS_BADGE[r.status] || ""}>{r.status}</Badge>
                {r.auto_booked && <span className="ml-1 text-[10px] text-violet-600">auto</span>}
                {r.status === "rejected" && r.rejection_reason && (
                  <div className="text-[10px] text-red-600 mt-1 max-w-[220px]">{r.rejection_reason}</div>
                )}
              </td>
              <td className="px-4 py-2 text-xs text-muted-foreground max-w-[240px]">{r.notes || "—"}</td>
              <td className="px-4 py-2 text-right whitespace-nowrap">
                {scope === "mine" && (r.status === "pending" || r.status === "approved") && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onCancel(r.id)} data-testid={`cancel-${r.id}`}>Cancel</Button>
                )}
                {scope === "team" && r.status === "pending" && (
                  <div className="flex justify-end gap-1">
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onApprove(r.id)} data-testid={`approve-${r.id}`}><CheckCircle2 size={12} className="mr-1"/>Approve</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => onReject(r.id)} data-testid={`reject-${r.id}`}><XCircle size={12} className="mr-1"/>Reject</Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ToilTable({ rows }) {
  if (!rows || rows.length === 0) return (
    <div className="border border-border rounded-md p-6 bg-white text-center text-sm text-muted-foreground" data-testid="toil-empty">
      No TOIL entries yet.
    </div>
  );
  return (
    <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="toil-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left">
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Worked date</th>
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Hours</th>
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Description</th>
          <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Status</th>
        </tr></thead>
        <tbody>
          {rows.map(t => (
            <tr key={t.id} className="border-b border-border" data-testid={`toil-row-${t.id}`}>
              <td className="px-4 py-2">{t.worked_date}</td>
              <td className="px-4 py-2 font-medium">{t.hours}h</td>
              <td className="px-4 py-2 text-xs text-muted-foreground max-w-[300px]">{t.description || "—"}</td>
              <td className="px-4 py-2">
                <Badge variant="outline" className={STATUS_BADGE[t.status] || ""}>{t.status}</Badge>
                {t.status === "rejected" && t.rejection_reason && (
                  <div className="text-[10px] text-red-600 mt-1">{t.rejection_reason}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TeamSummary({ rows }) {
  const today = new Date().toISOString().slice(0, 10);
  const in7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const pending = (rows || []).filter((r) => r.status === "pending");
  const offToday = (rows || []).filter((r) => r.status === "approved" && r.start_date <= today && today <= r.end_date);
  const offNext7 = (rows || []).filter((r) => r.status === "approved" && r.start_date <= in7 && r.end_date >= today);
  const uniqueOffToday = new Set(offToday.map((r) => r.user_id)).size;
  const uniqueOffNext7 = new Set(offNext7.map((r) => r.user_id)).size;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-testid="team-summary">
      <div className="border border-amber-200 rounded-md p-4 bg-amber-50">
        <p className="text-xs text-amber-800 uppercase tracking-wide mb-1">Awaiting your approval</p>
        <p className="font-heading font-bold text-2xl text-amber-700">{pending.length}</p>
        <p className="text-[11px] text-amber-600 mt-0.5">Pending requests to decide</p>
      </div>
      <div className="border border-sky-200 rounded-md p-4 bg-sky-50">
        <p className="text-xs text-sky-800 uppercase tracking-wide mb-1">Off today</p>
        <p className="font-heading font-bold text-2xl text-sky-700">{uniqueOffToday}</p>
        <p className="text-[11px] text-sky-600 mt-0.5">{offToday.map((r) => r.user_name).slice(0, 3).join(", ") || "Nobody off"}</p>
      </div>
      <div className="border border-indigo-200 rounded-md p-4 bg-indigo-50">
        <p className="text-xs text-indigo-800 uppercase tracking-wide mb-1">Off in the next 7 days</p>
        <p className="font-heading font-bold text-2xl text-indigo-700">{uniqueOffNext7}</p>
        <p className="text-[11px] text-indigo-600 mt-0.5">Staff with approved bookings</p>
      </div>
      <div className="border border-slate-200 rounded-md p-4 bg-white">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total requests logged</p>
        <p className="font-heading font-bold text-2xl">{rows?.length || 0}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">Across every status</p>
      </div>
    </div>
  );
}

